import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CategoryContext } from '../../providers/CategoryContext';
import { AuthContext } from '../../providers/AuthContext';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { showError, showSuccess } from '../../utils/sweetAlert';
import { DEFAULT_TYPOGRAPHY, GOOGLE_FONT_OPTIONS, fontStack } from '../../utils/googleFonts';
import { applyTypography } from '../shared/GlobalTypography';
import { applyColors } from '../shared/GlobalColors';
import { DEFAULT_COLORS, useSettings } from '../../providers/SettingsContext';
import EnhancedMediaLibrary from './EnhancedMediaLibrary';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const AI_SHOWCASE_SLOTS = [
  {
    key: 'futuristicAbstract',
    title: 'Futurismo Abstracto',
    description: 'Imagen principal para la tarjeta de tendencia IA.'
  },
  {
    key: 'livingWatercolors',
    title: 'Acuarelas Vivas',
    description: 'Imagen principal para la tarjeta de arte digital.'
  }
];

const PERSONALIZADOS_SLOTS = [
  {
    key: 'products',
    title: 'Productos Personalizados',
    description: 'Card “Personalizá nuestros productos” de la landing.',
    fallback: '/images/customization/tshirt-front-white-polyester.webp'
  },
  {
    key: 'ai',
    title: 'Personalizados IA',
    description: 'Card “Crealo con Inteligencia Artificial” de la landing.',
    fallback: '/images/categories/personalizados.png'
  },
  {
    key: 'merchandising',
    title: 'Merchandising',
    description: 'Card “Merchandising para tu marca” de la landing.',
    fallback: '/images/customization/hoodie-front-neutral.webp'
  }
];

const DEFAULT_HOME_SLIDES = [
  {
    id: 'slide-1',
    title: 'Colección Exclusiva',
    discount: 'Hasta 30% OFF',
    icon: 'fa-solid fa-star',
    backgroundColor: '#388E3C',
    backgroundImage: '/images/slider/sticker1.jpg',
    isActive: true
  },
  {
    id: 'slide-2',
    title: 'Estilo Urbano Premium',
    discount: 'Envíos a todo el país',
    icon: 'fa-solid fa-truck-fast',
    backgroundColor: '#43A047',
    backgroundImage: '/images/slider/sticker2.jpg',
    isActive: true
  },
  {
    id: 'slide-3',
    title: 'Decora tu Hogar',
    discount: 'Cuadros y Almohadones',
    icon: 'fa-solid fa-couch',
    backgroundColor: '#2E7D32',
    backgroundImage: '/images/slider/sticker3.jpg',
    isActive: true
  },
  {
    id: 'slide-4',
    title: 'Únete a la Revolución IA',
    discount: 'Diseños Únicos',
    icon: 'fa-brands fa-reddit-alien',
    backgroundColor: '#1B5E20',
    backgroundImage: '/images/slider/sticker4.jpg',
    isActive: true
  }
];

const normalizeKey = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'default';

const defaultCategoryImage = (name) => `/images/categories/${normalizeKey(name)}.png`;

const AdminSettings = () => {
  const { categories } = useContext(CategoryContext);
  const { token } = useContext(AuthContext);
  const { refreshSettings } = useSettings();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busySlot, setBusySlot] = useState(null);
  const [selectedCategoryImageSlot, setSelectedCategoryImageSlot] = useState(null);
  const [selectedPersonalizadosSlot, setSelectedPersonalizadosSlot] = useState(null);
  const [prompts, setPrompts] = useState({});
  const [referenceImages, setReferenceImages] = useState({});
  const [activeSettingsTab, setActiveSettingsTab] = useState('home-images');
  const [typographyDraft, setTypographyDraft] = useState(DEFAULT_TYPOGRAPHY);
  const [savingTypography, setSavingTypography] = useState(false);
  const [sliderDraft, setSliderDraft] = useState(DEFAULT_HOME_SLIDES);
  const [savingSlider, setSavingSlider] = useState(false);
  const [uploadingSliderId, setUploadingSliderId] = useState(null);
  const [colorsDraft, setColorsDraft] = useState(DEFAULT_COLORS);
  const [savingColors, setSavingColors] = useState(false);
  const [maintenanceDraft, setMaintenanceDraft] = useState({ isEnabled: false, title: 'Sitio en mantenimiento', message: 'Volvemos pronto. ¡Gracias por tu paciencia!' });
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [popupDraft, setPopupDraft] = useState({ isEnabled: false, imageUrl: '' });
  const [savingPopup, setSavingPopup] = useState(false);
  const [uploadingPopupImage, setUploadingPopupImage] = useState(false);
  const [shippingCostsDraft, setShippingCostsDraft] = useState({ dac_montevideo: 290, envio_interior_dac: 290, envio_interior_domicilio: 360 });
  const [savingShippingCosts, setSavingShippingCosts] = useState(false);

  const categorySlots = useMemo(() => {
    const slots = [
      { _id: 'tienda', name: 'Tienda', description: 'Acceso general a la tienda', slotKey: 'tienda' }
    ];

    categories.forEach((category) => {
      slots.push({
        _id: category._id,
        name: category.name,
        description: category.description || `Categoría ${category.name}`,
        slotKey: normalizeKey(category.name)
      });

      (category.subcategories || []).forEach((sub) => {
        slots.push({
          _id: `${category._id}-${sub._id || sub.slug || sub.name}`,
          name: `${category.name} · ${sub.name}`,
          description: sub.description || `Subcategoría de ${category.name}`,
          slotKey: normalizeKey(`${category.name}-${sub.name}`)
        });
      });
    });

    return slots;
  }, [categories]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/settings`);
      const data = await response.json();
      setSettings(data);
      setTypographyDraft({
        ...DEFAULT_TYPOGRAPHY,
        ...(data?.typography || {})
      });
      setSliderDraft(data?.homeSlider?.slides?.length ? data.homeSlider.slides : DEFAULT_HOME_SLIDES);
      setColorsDraft({ ...DEFAULT_COLORS, ...(data?.colors || {}) });
      setMaintenanceDraft({
        isEnabled: data?.maintenanceMode?.isEnabled || false,
        title: data?.maintenanceMode?.title || 'Sitio en mantenimiento',
        message: data?.maintenanceMode?.message || 'Volvemos pronto. ¡Gracias por tu paciencia!'
      });
      setPopupDraft({
        isEnabled: data?.promoPopup?.isEnabled || false,
        imageUrl: data?.promoPopup?.imageUrl || ''
      });
      setShippingCostsDraft({
        dac_montevideo: data?.shippingCosts?.dac_montevideo ?? 290,
        envio_interior_dac: data?.shippingCosts?.envio_interior_dac ?? 290,
        envio_interior_domicilio: data?.shippingCosts?.envio_interior_domicilio ?? 360
      });
    } catch (error) {
      console.error('Error loading settings:', error);
      showError('No se pudieron cargar las configuraciones');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryImage = (categoryName) => {
    const key = normalizeKey(categoryName);
    return settings?.homeImages?.categories?.[key] || defaultCategoryImage(categoryName);
  };

  const getAiShowcaseImage = (slotKey) => settings?.homeImages?.aiShowcase?.[slotKey];
  const getPersonalizadosImage = (slot) => settings?.homeImages?.personalizados?.[slot.key] || slot.fallback;

  const updateSettingsFromResponse = (data) => {
    if (data?.settings) {
      setSettings(data.settings);
    }
  };

  const handlePersonalizadosMediaSelect = async (media) => {
    if (!selectedPersonalizadosSlot || !media?._id) return;

    const busyKey = `personalizados-${selectedPersonalizadosSlot.key}`;
    setBusySlot(busyKey);
    try {
      const response = await fetch(`${API_BASE}/settings/home-image/select`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          slotType: 'personalizados',
          slotKey: selectedPersonalizadosSlot.key,
          mediaId: media._id
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo seleccionar la imagen');

      updateSettingsFromResponse(data);
      await refreshSettings();
      setSelectedPersonalizadosSlot(null);
      showSuccess('Imagen de Personalizados actualizada');
    } catch (error) {
      console.error('Error selecting Personalizados image:', error);
      showError(error.message || 'No se pudo seleccionar la imagen');
    } finally {
      setBusySlot(null);
    }
  };

  const handleCategoryImageMediaSelect = async (media) => {
    if (!selectedCategoryImageSlot || !media?._id) return;

    const { slotKey, title } = selectedCategoryImageSlot;
    const busyKey = `category-${slotKey}`;
    setBusySlot(busyKey);

    try {
      const response = await fetch(`${API_BASE}/settings/home-image/select`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          slotType: 'category',
          slotKey,
          mediaId: media._id
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo seleccionar la imagen');

      updateSettingsFromResponse(data);
      await refreshSettings();
      setSelectedCategoryImageSlot(null);
      showSuccess(`Imagen de ${title} actualizada`);
    } catch (error) {
      console.error('Error selecting category image:', error);
      showError(error.message || 'No se pudo seleccionar la imagen');
    } finally {
      setBusySlot(null);
    }
  };

  const handleUpload = async ({ slotType, slotKey, file }) => {
    if (!file) return;

    const busyKey = `${slotType}-${slotKey}`;
    setBusySlot(busyKey);

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('slotType', slotType);
      formData.append('slotKey', slotKey);

      const response = await fetch(`${API_BASE}/settings/home-image/upload`, {
        method: 'POST',
        headers: getAdminAuthHeaders(token),
        body: formData
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo subir la imagen');
      }

      updateSettingsFromResponse(data);
      await refreshSettings();
      showSuccess('Imagen actualizada con éxito');
    } catch (error) {
      console.error('Error uploading home image:', error);
      showError(error.message || 'No se pudo subir la imagen');
    } finally {
      setBusySlot(null);
    }
  };

  const handleGenerate = async ({ slotType, slotKey }) => {
    const promptKey = `${slotType}-${slotKey}`;
    const prompt = prompts[promptKey]?.trim();

    if (!prompt) {
      showError('Escribe un prompt para generar la imagen');
      return;
    }

    setBusySlot(promptKey);

    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      formData.append('slotType', slotType);
      formData.append('slotKey', slotKey);

      if (referenceImages[promptKey]) {
        formData.append('referenceImage', referenceImages[promptKey]);
      }

      const response = await fetch(`${API_BASE}/settings/home-image/generate`, {
        method: 'POST',
        headers: getAdminAuthHeaders(token),
        body: formData
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo generar la imagen');
      }

      updateSettingsFromResponse(data);
      await refreshSettings();
      showSuccess('Imagen generada y aplicada con éxito');
    } catch (error) {
      console.error('Error generating image:', error);
      showError(error.message || 'No se pudo generar la imagen');
    } finally {
      setBusySlot(null);
    }
  };

  const handleTypographyChange = (field, value) => {
    const nextTypography = {
      ...typographyDraft,
      [field]: value
    };

    setTypographyDraft(nextTypography);
    applyTypography(nextTypography);
  };

  const handleTypographySave = async () => {
    setSavingTypography(true);

    try {
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ typography: typographyDraft })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudieron guardar las tipografías');
      }

      setSettings(data);
      applyTypography(data.typography);
      showSuccess('Tipografías actualizadas con éxito');
    } catch (error) {
      console.error('Error saving typography:', error);
      showError(error.message || 'No se pudieron guardar las tipografías');
    } finally {
      setSavingTypography(false);
    }
  };

  const handleSlideChange = (slideId, field, value) => {
    setSliderDraft(prev =>
      prev.map(slide =>
        slide.id === slideId
          ? { ...slide, [field]: value }
          : slide
      )
    );
  };

  const handleAddSlide = () => {
    const nextNumber = sliderDraft.length + 1;
    setSliderDraft(prev => [
      ...prev,
      {
        id: `slide-${Date.now()}`,
        title: `Nuevo Slide ${nextNumber}`,
        discount: 'Texto secundario',
        icon: 'fa-solid fa-star',
        backgroundColor: '#1f8e38',
        backgroundImage: '/images/slider/sticker1.jpg',
        isActive: true
      }
    ]);
  };

  const handleRemoveSlide = (slideId) => {
    setSliderDraft(prev => prev.filter(slide => slide.id !== slideId));
  };

  const handleSliderImageUpload = async (slideId, file) => {
    if (!file) return;

    setUploadingSliderId(slideId);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_BASE}/settings/home-slider/upload`, {
        method: 'POST',
        headers: getAdminAuthHeaders(token),
        body: formData
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo subir la imagen del slide');
      }

      handleSlideChange(slideId, 'backgroundImage', data.imageUrl);
      showSuccess('Imagen del slide subida con éxito');
    } catch (error) {
      console.error('Error uploading slider image:', error);
      showError(error.message || 'No se pudo subir la imagen del slide');
    } finally {
      setUploadingSliderId(null);
    }
  };

  const handleSliderSave = async () => {
    const validSlides = sliderDraft.filter(slide => slide.title?.trim() && slide.backgroundImage?.trim());

    if (validSlides.length === 0) {
      showError('Deja al menos un slide con título e imagen');
      return;
    }

    setSavingSlider(true);

    try {
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ homeSlider: { slides: validSlides } })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo guardar el slider');
      }

      setSettings(data);
      setSliderDraft(data?.homeSlider?.slides?.length ? data.homeSlider.slides : validSlides);
      showSuccess('Slider de home actualizado con éxito');
    } catch (error) {
      console.error('Error saving slider:', error);
      showError(error.message || 'No se pudo guardar el slider');
    } finally {
      setSavingSlider(false);
    }
  };

  const handleColorsSave = async () => {
    setSavingColors(true);
    try {
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ colors: colorsDraft })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudieron guardar los colores');
      setSettings(data);
      applyColors(data.colors);
      showSuccess('Colores actualizados con éxito');
    } catch (error) {
      showError(error.message || 'No se pudieron guardar los colores');
    } finally {
      setSavingColors(false);
    }
  };

  const handleShippingCostsSave = async () => {
    const normalized = Object.fromEntries(Object.entries(shippingCostsDraft).map(([key, value]) => [key, Number(value)]));
    if (Object.values(normalized).some(value => !Number.isFinite(value) || value < 0)) {
      showError('Ingresá costos de envío válidos');
      return;
    }

    setSavingShippingCosts(true);
    try {
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ shippingCosts: normalized })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudieron guardar los costos de envío');
      setSettings(data);
      setShippingCostsDraft(data.shippingCosts || normalized);
      await refreshSettings();
      showSuccess('Costos de envío actualizados');
    } catch (error) {
      showError(error.message || 'No se pudieron guardar los costos de envío');
    } finally {
      setSavingShippingCosts(false);
    }
  };

  const handleMaintenanceSave = async () => {
    setSavingMaintenance(true);
    try {
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ maintenanceMode: maintenanceDraft })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudieron guardar los ajustes');
      setSettings(data);
      showSuccess('Configuración de mantenimiento guardada');
    } catch (error) {
      showError(error.message || 'No se pudieron guardar los ajustes');
    } finally {
      setSavingMaintenance(false);
    }
  };

  const handlePopupImageUpload = async (file) => {
    if (!file) return;
    setUploadingPopupImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch(`${API_BASE}/settings/popup/upload`, {
        method: 'POST',
        headers: getAdminAuthHeaders(token),
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo subir la imagen');
      setPopupDraft(prev => ({ ...prev, imageUrl: data.imageUrl }));
      if (data.settings) setSettings(data.settings);
      showSuccess('Imagen del popup subida con éxito');
    } catch (error) {
      showError(error.message || 'No se pudo subir la imagen');
    } finally {
      setUploadingPopupImage(false);
    }
  };

  const handlePopupSave = async () => {
    setSavingPopup(true);
    try {
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ promoPopup: popupDraft })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo guardar el popup');
      setSettings(data);
      showSuccess('Configuración del popup guardada');
    } catch (error) {
      showError(error.message || 'No se pudo guardar el popup');
    } finally {
      setSavingPopup(false);
    }
  };

  const renderTypographySelect = ({ label, field, description }) => (
    <label className="admin-settings-font-field">
      <span>{label}</span>
      <small>{description}</small>
      <select
        value={typographyDraft[field]}
        onChange={(event) => handleTypographyChange(field, event.target.value)}
      >
        {GOOGLE_FONT_OPTIONS.map(font => (
          <option key={font} value={font}>{font}</option>
        ))}
      </select>
    </label>
  );

  const renderImageControls = ({ slotType, slotKey, imageUrl, title, description }) => {
    const promptKey = `${slotType}-${slotKey}`;
    const isBusy = busySlot === promptKey;

    return (
      <div className="admin-settings-card" key={promptKey}>
        <div className="admin-settings-preview">
          <img src={getDisplayImageUrl(imageUrl)} alt={title} />
        </div>
        <div className="admin-settings-card-body">
          <div>
            <h3>{title}</h3>
            {description && <p>{description}</p>}
          </div>

          <label className="admin-settings-upload">
            <i className="fas fa-upload"></i>
            <span>Subir imagen</span>
            <input
              type="file"
              accept="image/*"
              onChange={(event) => handleUpload({
                slotType,
                slotKey,
                file: event.target.files?.[0]
              })}
              disabled={isBusy}
            />
          </label>

          <div className="admin-settings-generator">
            <textarea
              value={prompts[promptKey] || ''}
              onChange={(event) => setPrompts(prev => ({ ...prev, [promptKey]: event.target.value }))}
              placeholder="Prompt para Gemini. Ej: diseño abstracto futurista en verde y negro, ideal para remera..."
              rows="3"
              disabled={isBusy}
            />
            <div className="admin-settings-reference-row">
              <label>
                Imagen guía opcional
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setReferenceImages(prev => ({
                    ...prev,
                    [promptKey]: event.target.files?.[0] || null
                  }))}
                  disabled={isBusy}
                />
              </label>
            </div>
            <button
              type="button"
              className="admin-settings-generate-btn"
              onClick={() => handleGenerate({ slotType, slotKey })}
              disabled={isBusy}
            >
              {isBusy ? (
                <><i className="fas fa-spinner fa-spin"></i> Generando...</>
              ) : (
                <><i className="fas fa-wand-magic-sparkles"></i> Generar con Gemini</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryImageControl = (category) => {
    const slotKey = category.slotKey || normalizeKey(category.name);
    const busyKey = `category-${slotKey}`;
    const isBusy = busySlot === busyKey;

    return (
      <div className="admin-settings-card" key={busyKey}>
        <div className="admin-settings-preview">
          <img
            src={getDisplayImageUrl(settings?.homeImages?.categories?.[slotKey] || defaultCategoryImage(category.name))}
            alt={category.name}
          />
        </div>
        <div className="admin-settings-card-body">
          <div>
            <h3>{category.name}</h3>
            {category.description && <p>{category.description}</p>}
          </div>
          <button
            type="button"
            className="admin-settings-upload"
            onClick={() => setSelectedCategoryImageSlot({ slotKey, title: category.name })}
            disabled={isBusy}
          >
            <i className={isBusy ? 'fas fa-spinner fa-spin' : 'fas fa-images'}></i>
            <span>{isBusy ? 'Guardando...' : 'Seleccionar de la biblioteca'}</span>
          </button>
        </div>
      </div>
    );
  };

  const renderPersonalizadosImageControl = (slot) => {
    const busyKey = `personalizados-${slot.key}`;
    const isBusy = busySlot === busyKey;

    return (
      <div className="admin-settings-card" key={slot.key}>
        <div className="admin-settings-preview">
          <img src={getDisplayImageUrl(getPersonalizadosImage(slot))} alt={slot.title} />
        </div>
        <div className="admin-settings-card-body">
          <div>
            <h3>{slot.title}</h3>
            <p>{slot.description}</p>
          </div>
          <button
            type="button"
            className="admin-settings-upload"
            onClick={() => setSelectedPersonalizadosSlot(slot)}
            disabled={isBusy}
          >
            <i className={isBusy ? 'fas fa-spinner fa-spin' : 'fas fa-images'}></i>
            <span>{isBusy ? 'Guardando...' : 'Seleccionar de la biblioteca'}</span>
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-settings-loading">
        <i className="fas fa-spinner fa-spin"></i> Cargando configuraciones...
      </div>
    );
  }

  return (
    <div className="admin-settings-panel">
      <div className="dashboard-header">
        <h1><i className="fas fa-gear" style={{ marginRight: '10px' }}></i>Configuraciones</h1>
        <p>Gestiona imágenes y configuraciones visuales de la web.</p>
      </div>

      <div className="admin-settings-tabs" role="tablist" aria-label="Configuraciones del sitio">
        <button
          type="button"
          className={activeSettingsTab === 'home-images' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('home-images')}
        >
          <i className="fas fa-images"></i>
          Imágenes de Home
        </button>
        <button
          type="button"
          className={activeSettingsTab === 'personalizados-images' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('personalizados-images')}
        >
          <i className="fas fa-pen-ruler"></i>
          Personalizados
        </button>
        <button
          type="button"
          className={activeSettingsTab === 'typography' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('typography')}
        >
          <i className="fas fa-font"></i>
          Tipografías
        </button>
        <button
          type="button"
          className={activeSettingsTab === 'home-slider' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('home-slider')}
        >
          <i className="fas fa-sliders"></i>
          Slider Home
        </button>
        <button
          type="button"
          className={activeSettingsTab === 'colors' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('colors')}
        >
          <i className="fas fa-palette"></i>
          Colores
        </button>
        <button
          type="button"
          className={activeSettingsTab === 'shipping' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('shipping')}
        >
          <i className="fas fa-truck"></i>
          Envíos
        </button>
        <button
          type="button"
          className={activeSettingsTab === 'maintenance' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('maintenance')}
        >
          <i className="fas fa-wrench"></i>
          Mantenimiento
        </button>
        <button
          type="button"
          className={activeSettingsTab === 'popup' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('popup')}
        >
          <i className="fas fa-rectangle-ad"></i>
          Popup
        </button>
      </div>

      {activeSettingsTab === 'home-images' && (
        <>
          <section className="admin-settings-section">
            <div className="admin-settings-section-header">
              <h2>Imágenes de categorías</h2>
              <button type="button" onClick={fetchSettings}>
                <i className="fas fa-rotate"></i> Actualizar
              </button>
            </div>
            <p style={{ marginTop: 0, color: '#6b7280' }}>
              Incluye subcategorías (ej. Niños · Canguros). Elegí una imagen ya cargada en la Biblioteca multimedia; se usará en la grilla de la home.
            </p>
            <div className="admin-settings-grid">
              {categorySlots.map(renderCategoryImageControl)}
            </div>
          </section>

          <section className="admin-settings-section">
            <div className="admin-settings-section-header">
              <h2>Sección de IA</h2>
            </div>
            <div className="admin-settings-grid admin-settings-grid-two">
              {AI_SHOWCASE_SLOTS.map(slot => renderImageControls({
                slotType: 'aiShowcase',
                slotKey: slot.key,
                title: slot.title,
                description: slot.description,
                imageUrl: getAiShowcaseImage(slot.key)
              }))}
            </div>
          </section>
        </>
      )}

      {activeSettingsTab === 'personalizados-images' && (
        <section className="admin-settings-section">
          <div className="admin-settings-section-header">
            <div>
              <h2>Imágenes de la landing Personalizados</h2>
              <p className="admin-settings-section-subtitle">Administrá las imágenes de las tres opciones principales de `/personalizados`.</p>
            </div>
            <button type="button" onClick={fetchSettings}>
              <i className="fas fa-rotate"></i> Actualizar
            </button>
          </div>
          <div className="admin-settings-grid">
            {PERSONALIZADOS_SLOTS.map(renderPersonalizadosImageControl)}
          </div>
        </section>
      )}

      {activeSettingsTab === 'typography' && (
        <section className="admin-settings-section">
          <div className="admin-settings-section-header">
            <h2>Tipografías de Google Fonts</h2>
            <button type="button" onClick={handleTypographySave} disabled={savingTypography}>
              {savingTypography ? (
                <><i className="fas fa-spinner fa-spin"></i> Guardando...</>
              ) : (
                <><i className="fas fa-save"></i> Guardar tipografías</>
              )}
            </button>
          </div>

          <div className="admin-settings-font-layout">
            <div className="admin-settings-font-controls">
              {renderTypographySelect({
                label: 'Texto general',
                field: 'bodyFont',
                description: 'Párrafos, contenido y textos largos.'
              })}
              {renderTypographySelect({
                label: 'Títulos',
                field: 'headingFont',
                description: 'H1, H2, títulos de secciones y encabezados.'
              })}
              {renderTypographySelect({
                label: 'Acentos e interfaz',
                field: 'accentFont',
                description: 'Botones, navegación, badges, inputs y precios.'
              })}
            </div>

            <div className="admin-settings-font-preview">
              <span style={{ fontFamily: fontStack(typographyDraft.accentFont) }}>Vista previa</span>
              <h3 style={{ fontFamily: fontStack(typographyDraft.headingFont) }}>REM Enterprises</h3>
              <p style={{ fontFamily: fontStack(typographyDraft.bodyFont) }}>
                Esta combinación se aplicará a toda la web: tienda, home, productos, checkout y dashboard.
              </p>
              <button type="button" style={{ fontFamily: fontStack(typographyDraft.accentFont) }}>
                Botón de ejemplo
              </button>
            </div>
          </div>
        </section>
      )}

      {activeSettingsTab === 'home-slider' && (
        <section className="admin-settings-section">
          <div className="admin-settings-section-header">
            <div>
              <h2>Slider de la home</h2>
              <p className="admin-settings-section-subtitle">Edita imágenes, textos, iconos y cantidad de slides.</p>
            </div>
            <div className="admin-settings-header-actions">
              <button type="button" onClick={handleAddSlide}>
                <i className="fas fa-plus"></i> Agregar slide
              </button>
              <button type="button" onClick={handleSliderSave} disabled={savingSlider}>
                {savingSlider ? (
                  <><i className="fas fa-spinner fa-spin"></i> Guardando...</>
                ) : (
                  <><i className="fas fa-save"></i> Guardar slider</>
                )}
              </button>
            </div>
          </div>

          <div className="admin-settings-slider-list">
            {sliderDraft.map((slide, index) => {
              const isUploading = uploadingSliderId === slide.id;

              return (
                <div className="admin-settings-slider-card" key={slide.id}>
                  <div className="admin-settings-slider-preview">
                    <img src={getDisplayImageUrl(slide.backgroundImage)} alt={slide.title} />
                    <div className="admin-settings-slider-preview-overlay">
                      <i className={`${slide.icon || 'fa-solid fa-star'}`}></i>
                      <strong>{slide.title}</strong>
                      <span>{slide.discount}</span>
                    </div>
                  </div>

                  <div className="admin-settings-slider-fields">
                    <div className="admin-settings-slider-title-row">
                      <div>
                        <span className="admin-settings-slide-kicker">Slide {index + 1}</span>
                        <h3>{slide.title || `Slide ${index + 1}`}</h3>
                      </div>
                      <div className="admin-settings-slider-actions">
                        <label className="admin-settings-switch">
                          <input
                            type="checkbox"
                            checked={slide.isActive !== false}
                            onChange={(event) => handleSlideChange(slide.id, 'isActive', event.target.checked)}
                          />
                          <span>Activo</span>
                        </label>
                        <button
                          type="button"
                          className="admin-settings-remove-slide"
                          onClick={() => handleRemoveSlide(slide.id)}
                          disabled={sliderDraft.length <= 1}
                          title="Eliminar slide"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>

                    <div className="admin-settings-form-grid">
                      <label>
                        Título
                        <input
                          type="text"
                          value={slide.title}
                          onChange={(event) => handleSlideChange(slide.id, 'title', event.target.value)}
                        />
                      </label>
                      <label>
                        Texto secundario
                        <input
                          type="text"
                          value={slide.discount}
                          onChange={(event) => handleSlideChange(slide.id, 'discount', event.target.value)}
                        />
                      </label>
                      <label>
                        Icono FontAwesome
                        <input
                          type="text"
                          value={slide.icon}
                          onChange={(event) => handleSlideChange(slide.id, 'icon', event.target.value)}
                          placeholder="fa-solid fa-star"
                        />
                      </label>
                      <label>
                        Color de respaldo
                        <input
                          type="color"
                          value={slide.backgroundColor || '#1f8e38'}
                          onChange={(event) => handleSlideChange(slide.id, 'backgroundColor', event.target.value)}
                        />
                      </label>
                    </div>

                    <div className="admin-settings-slider-bottom-row">
                      <label className="admin-settings-upload admin-settings-slider-upload">
                        <i className={isUploading ? 'fas fa-spinner fa-spin' : 'fas fa-upload'}></i>
                        <span>{isUploading ? 'Subiendo imagen...' : 'Cambiar imagen'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(event) => handleSliderImageUpload(slide.id, event.target.files?.[0])}
                          disabled={isUploading}
                        />
                      </label>
                      <span className={`admin-settings-slide-status ${slide.isActive !== false ? 'active' : 'inactive'}`}>
                        {slide.isActive !== false ? 'Visible en home' : 'Oculto'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
      {activeSettingsTab === 'colors' && (
        <section className="admin-settings-section">
          <div className="admin-settings-section-header">
            <div>
              <h2>Paleta de colores</h2>
              <p className="admin-settings-section-subtitle">Los cambios se aplican a toda la web en tiempo real.</p>
            </div>
            <button type="button" onClick={handleColorsSave} disabled={savingColors}>
              {savingColors ? (
                <><i className="fas fa-spinner fa-spin"></i> Guardando...</>
              ) : (
                <><i className="fas fa-save"></i> Guardar colores</>
              )}
            </button>
          </div>

          <div className="admin-settings-form-grid" style={{ maxWidth: '680px' }}>
            {[
              { key: 'primary',       label: 'Color primario',        desc: 'Botones, links y acentos principales.' },
              { key: 'primaryDark',   label: 'Color primario oscuro', desc: 'Hover en botones y bordes activos.' },
              { key: 'bg',            label: 'Fondo del sitio',       desc: 'Color de fondo general de las páginas.' },
              { key: 'textMain',      label: 'Texto principal',       desc: 'Títulos y texto de mayor jerarquía.' },
              { key: 'textSecondary', label: 'Texto secundario',      desc: 'Subtítulos, descripciones y metadatos.' },
              { key: 'price',         label: 'Color de precios',      desc: 'Precios de productos en la tienda.' },
            ].map(({ key, label, desc }) => (
              <label key={key}>
                <span style={{ fontWeight: 600, fontSize: '0.88rem', display: 'block', marginBottom: '2px' }}>{label}</span>
                <small style={{ color: '#6b7280', fontSize: '0.78rem', display: 'block', marginBottom: '8px' }}>{desc}</small>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="color"
                    value={colorsDraft[key] || '#000000'}
                    onChange={(e) => {
                      const next = { ...colorsDraft, [key]: e.target.value };
                      setColorsDraft(next);
                      applyColors(next);
                    }}
                    style={{ width: '48px', height: '40px', padding: '2px', borderRadius: '8px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={colorsDraft[key] || ''}
                    onChange={(e) => {
                      const next = { ...colorsDraft, [key]: e.target.value };
                      setColorsDraft(next);
                      if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) applyColors(next);
                    }}
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', fontFamily: 'monospace' }}
                    placeholder="#000000"
                  />
                </div>
              </label>
            ))}
          </div>
        </section>
      )}

      {activeSettingsTab === 'shipping' && (
        <section className="admin-settings-section">
          <div className="admin-settings-section-header">
            <div>
              <h2>Costos de envío</h2>
              <p className="admin-settings-section-subtitle">Estos importes se aplican automáticamente en el checkout y se validan nuevamente al crear el pago.</p>
            </div>
            <button type="button" onClick={handleShippingCostsSave} disabled={savingShippingCosts}>
              {savingShippingCosts ? (
                <><i className="fas fa-spinner fa-spin"></i> Guardando...</>
              ) : (
                <><i className="fas fa-save"></i> Guardar costos</>
              )}
            </button>
          </div>
          <div className="admin-settings-form-grid" style={{ maxWidth: '760px' }}>
            {[
              { key: 'dac_montevideo', label: 'Montevideo', description: 'Tarifa plana para entregas dentro de Montevideo.' },
              { key: 'envio_interior_dac', label: 'Interior — Agencia DAC', description: 'Envío para retirar en una agencia DAC del interior.' },
              { key: 'envio_interior_domicilio', label: 'Interior — Domicilio', description: 'Entrega a domicilio en departamentos del interior.' }
            ].map(option => (
              <label key={option.key}>
                <span style={{ fontWeight: 700, display: 'block', marginBottom: '4px' }}>{option.label}</span>
                <small style={{ color: '#6b7280', display: 'block', marginBottom: '8px' }}>{option.description}</small>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 700 }}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={shippingCostsDraft[option.key]}
                    onChange={(event) => setShippingCostsDraft(previous => ({ ...previous, [option.key]: event.target.value }))}
                  />
                </div>
              </label>
            ))}
          </div>
          <div style={{ marginTop: '20px', padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', color: '#166534' }}>
            <i className="fas fa-store" style={{ marginRight: '8px' }}></i>
            El retiro en pickup permanece gratis.
          </div>
        </section>
      )}

      {activeSettingsTab === 'maintenance' && (
        <section className="admin-settings-section">
          <div className="admin-settings-section-header">
            <div>
              <h2>Modo mantenimiento</h2>
              <p className="admin-settings-section-subtitle">Bloquea el acceso al sitio para visitantes. Los admins autenticados lo ven igual.</p>
            </div>
            <button type="button" onClick={handleMaintenanceSave} disabled={savingMaintenance}>
              {savingMaintenance ? (
                <><i className="fas fa-spinner fa-spin"></i> Guardando...</>
              ) : (
                <><i className="fas fa-save"></i> Guardar</>
              )}
            </button>
          </div>

          <div style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <label className="admin-settings-switch" style={{ fontSize: '1rem', fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={maintenanceDraft.isEnabled}
                onChange={(e) => setMaintenanceDraft(prev => ({ ...prev, isEnabled: e.target.checked }))}
              />
              <span>Activar modo mantenimiento</span>
              <span style={{
                marginLeft: 'auto',
                padding: '2px 10px',
                borderRadius: '99px',
                fontSize: '0.75rem',
                fontWeight: 700,
                background: maintenanceDraft.isEnabled ? '#fef2f2' : '#f0fdf4',
                color: maintenanceDraft.isEnabled ? '#dc2626' : 'var(--color-primary)',
                border: `1px solid ${maintenanceDraft.isEnabled ? '#fecaca' : '#bbf7d0'}`,
              }}>
                {maintenanceDraft.isEnabled ? 'ACTIVO' : 'INACTIVO'}
              </span>
            </label>

            <div className="admin-settings-form-grid" style={{ gridTemplateColumns: '1fr' }}>
              <label>
                Título de la página
                <input
                  type="text"
                  value={maintenanceDraft.title}
                  onChange={(e) => setMaintenanceDraft(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Sitio en mantenimiento"
                />
              </label>
              <label>
                Mensaje para visitantes
                <input
                  type="text"
                  value={maintenanceDraft.message}
                  onChange={(e) => setMaintenanceDraft(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Volvemos pronto. ¡Gracias por tu paciencia!"
                />
              </label>
            </div>

            <div style={{
              padding: '12px 16px',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '10px',
              color: '#92400e',
              fontSize: '0.85rem',
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-start',
            }}>
              <i className="fas fa-triangle-exclamation" style={{ marginTop: '2px', flexShrink: 0 }} />
              <span>Los administradores autenticados pueden navegar el sitio normalmente aunque el modo mantenimiento esté activo.</span>
            </div>
          </div>
        </section>
      )}

      {activeSettingsTab === 'popup' && (
        <section className="admin-settings-section">
          <div className="admin-settings-section-header">
            <div>
              <h2>Popup promocional</h2>
              <p className="admin-settings-section-subtitle">Se muestra una sola vez por sesión al entrar al sitio.</p>
            </div>
            <button type="button" onClick={handlePopupSave} disabled={savingPopup}>
              {savingPopup ? (
                <><i className="fas fa-spinner fa-spin"></i> Guardando...</>
              ) : (
                <><i className="fas fa-save"></i> Guardar</>
              )}
            </button>
          </div>

          <div className="admin-settings-popup-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '28px', maxWidth: '800px', alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <label className="admin-settings-switch" style={{ fontSize: '1rem', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={popupDraft.isEnabled}
                  onChange={(e) => setPopupDraft(prev => ({ ...prev, isEnabled: e.target.checked }))}
                />
                <span>Mostrar popup al entrar</span>
                <span style={{
                  marginLeft: 'auto',
                  padding: '2px 10px',
                  borderRadius: '99px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  background: popupDraft.isEnabled ? '#f0fdf4' : '#f9fafb',
                  color: popupDraft.isEnabled ? 'var(--color-primary)' : '#6b7280',
                  border: `1px solid ${popupDraft.isEnabled ? '#bbf7d0' : '#e5e7eb'}`,
                }}>
                  {popupDraft.isEnabled ? 'ACTIVO' : 'INACTIVO'}
                </span>
              </label>

              <div>
                <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>Imagen del popup</p>
                <label className="admin-settings-upload" style={{ maxWidth: '260px' }}>
                  <i className={uploadingPopupImage ? 'fas fa-spinner fa-spin' : 'fas fa-upload'}></i>
                  <span>{uploadingPopupImage ? 'Subiendo...' : 'Subir imagen'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePopupImageUpload(e.target.files?.[0])}
                    disabled={uploadingPopupImage}
                  />
                </label>
              </div>

              <div style={{
                padding: '12px 16px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '10px',
                color: '#166534',
                fontSize: '0.82rem',
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start',
              }}>
                <i className="fas fa-circle-info" style={{ marginTop: '2px', flexShrink: 0 }} />
                <span>El popup se muestra <strong>una sola vez por sesión</strong>. Si el usuario cierra el navegador y vuelve, verá el popup nuevamente.</span>
              </div>
            </div>

            <div style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '14px',
              overflow: 'hidden',
              aspectRatio: '3/4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {popupDraft.imageUrl ? (
                <img
                  src={getDisplayImageUrl(popupDraft.imageUrl)}
                  alt="Vista previa del popup"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#d1d5db', padding: '20px' }}>
                  <i className="fas fa-image" style={{ fontSize: '2.5rem', display: 'block', marginBottom: '8px' }} />
                  <span style={{ fontSize: '0.82rem' }}>Sin imagen</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {selectedCategoryImageSlot && (
        <div className="admin-settings-media-overlay" onClick={() => setSelectedCategoryImageSlot(null)}>
          <div className="admin-settings-media-modal" role="dialog" aria-modal="true" aria-labelledby="category-image-media-title" onClick={(event) => event.stopPropagation()}>
            <div className="admin-settings-media-header">
              <div>
                <h2 id="category-image-media-title">Seleccionar imagen</h2>
                <p>Elegí la imagen de Biblioteca para {selectedCategoryImageSlot.title}.</p>
              </div>
              <button type="button" onClick={() => setSelectedCategoryImageSlot(null)} aria-label="Cerrar biblioteca">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <EnhancedMediaLibrary mode="selection" allowUpload={false} onSelect={handleCategoryImageMediaSelect} />
          </div>
        </div>
      )}

      {selectedPersonalizadosSlot && (
        <div className="admin-settings-media-overlay" onClick={() => setSelectedPersonalizadosSlot(null)}>
          <div className="admin-settings-media-modal" role="dialog" aria-modal="true" aria-labelledby="personalizados-media-title" onClick={(event) => event.stopPropagation()}>
            <div className="admin-settings-media-header">
              <div>
                <h2 id="personalizados-media-title">Seleccionar imagen</h2>
                <p>Elegí la imagen para {selectedPersonalizadosSlot.title}.</p>
              </div>
              <button type="button" onClick={() => setSelectedPersonalizadosSlot(null)} aria-label="Cerrar biblioteca">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <EnhancedMediaLibrary mode="selection" allowUpload={false} onSelect={handlePersonalizadosMediaSelect} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
